import { z } from 'zod'

import type { GtmUserData } from './user-data'

export interface UserDataInput {
    email: string
    firstName?: string
    lastName?: string
}

const GMAIL_DOMAINS = new Set(['gmail.com', 'googlemail.com'])

/**
 * RFC 5321's upper bound on a forward-path address, over the RFC 5322 pattern
 * rather than Zod's stricter default — the default rejects local-part specials
 * (`&`, `=`, `%`, `!`) that are deliverable addresses.
 */
const emailSchema = z.email({ pattern: z.regexes.rfc5322Email }).max(254)

/**
 * Google's enhanced-conversions email normalization: trim, lowercase, and drop
 * the dots in a gmail/googlemail local part. TikTok does not strip dots, and one
 * digest can only follow one rule — Google is the primary channel, so a
 * dotted-gmail visitor matches on TikTok by `ttclid`/`_ttp` instead. Every other
 * Wanderu producer of this key must apply the same rule, or the digests will not
 * match each other. Google's Ads API doc adds a second gmail rule, dropping `+`
 * and what follows it in the local part; it is deliberately not applied, because
 * seo-data-transformer-pipeline's `normalize_and_hash_email_address` does not
 * apply it either and parity with that hasher is worth more than parity with the
 * API-side rule. Adopting it means changing every producer together.
 *
 * Structurally invalid addresses resolve to nothing: the session mirror hands
 * over a stored address no form in this app validated, and a malformed one must
 * be omitted rather than hashed into a digest that matches nobody.
 */
function normalizeEmail(email: string): string | null {
    const trimmed = email.trim().toLowerCase()
    if (!emailSchema.safeParse(trimmed).success) {
        return null
    }
    const at = trimmed.lastIndexOf('@')
    const domain = trimmed.slice(at + 1)
    const local = trimmed.slice(0, at)
    return GMAIL_DOMAINS.has(domain) ? `${local.replaceAll('.', '')}@${domain}` : trimmed
}

/** Lowercase hex SHA-256 of the UTF-8 bytes (`hashlib.sha256(s.encode()).hexdigest()`). */
export async function sha256Hex(value: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

async function hashOrOmit(value: string | undefined): Promise<string | undefined> {
    return value ? sha256Hex(value) : undefined
}

/**
 * Normalize per Google's enhanced-conversions rules and hash to the pre-hashed
 * `GtmUserData` shape. Works in the browser and on the server (Web Crypto), so
 * each producer hashes where the plaintext already is.
 *
 * Resolves `null` without a usable email — the caller reports nothing. A name
 * that does not normalize is omitted, never hashed as `''`: a hash of the empty
 * string is a value that matches nothing while looking like a supplied one.
 */
export async function normalizeAndHashUserData(input: UserDataInput): Promise<GtmUserData | null> {
    const email = normalizeEmail(input.email)
    if (!email) {
        return null
    }

    const [sha256_email_address, sha256_first_name, sha256_last_name] = await Promise.all([
        sha256Hex(email),
        hashOrOmit(input.firstName?.trim().toLowerCase()),
        hashOrOmit(input.lastName?.trim().toLowerCase()),
    ])

    const address = {
        ...(sha256_first_name && { sha256_first_name }),
        ...(sha256_last_name && { sha256_last_name }),
    }

    return {
        ...(Object.keys(address).length > 0 && { address }),
        sha256_email_address,
    }
}
